import { authFetch } from '../../../api/csrf';
import React, { useState, useEffect, useRef } from "react";
import { useSearchParams } from 'react-router-dom';
import {
  FileText, Plus, Search, Download, Eye, Edit, Trash2, CheckCircle, AlertCircle, ArrowLeft, Save, X, Loader2, Upload,
} from "lucide-react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import Button from "../../../components/common/Button/Button";
import PermissionGate from "../../../components/common/PermissionGate/PermissionGate";
import hrService from "../../../api/services/hrService";
import {
  policyService,
  acknowledgmentService,
} from "../../../api/services/complianceService";
import { toast } from 'react-hot-toast';
import { formatDate } from "../../../utils/stringUtils";
import { policyCategoryService } from "../../../api/crm/config/configService";
import {
  exportToCSV,
} from "../../../utils/exportUtils";
import ExportModal from "../../../components/common/ExportModal";
import PaginationControls from "../../../components/common/Pagination/PaginationControls";
import DateRangeFilter from "../../../components/common/DateRangeFilter";
import BulkUploadModal from "../../../components/common/BulkUploadModal";

// Custom debounce hook
const useDebounce = (value, delay) => {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
};

const PolicyLibrary = ({ userRole, initialMode, id, onClose, isDrawer }) => {
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const isListView = !isDrawer && !initialMode;
  const isInitialFilterMount = useRef(true);

  const [filteredPolicies, setFilteredPolicies] = useState([]);
  const [searchTerm, setSearchTerm] = useState(searchParams.get('search') || "");
  const [filterCategory, setFilterCategory] = useState(searchParams.get('category') || "all");
  const [filterStatus, setFilterStatus] = useState(searchParams.get('status') || "all");
  const [startDate, setStartDate] = useState(searchParams.get('startDate') || "");
  const [endDate, setEndDate] = useState(searchParams.get('endDate') || "");
  const [page, setPage] = useState(Number(searchParams.get('page')) || 1);
  const [limit, setLimit] = useState(Number(searchParams.get('limit')) || 10);
  const [showPolicyForm, setShowPolicyForm] = useState(false);
  const [editingPolicy, setEditingPolicy] = useState(null);
  const [selectedPolicy, setSelectedPolicy] = useState(null);
  const [showDetails, setShowDetails] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [policyToDelete, setPolicyToDelete] = useState(null);
  const [formData, setFormData] = useState({
    title: "",
    category: "",
    description: "",
    pdfFile: null,
    effectiveDate: "",
    status: "draft",
    existingPdfUrl: null,
    pdfPublicId: null,
  });
  const [uploadingPdf, setUploadingPdf] = useState(false);
  const [deletingPdf, setDeletingPdf] = useState(false);
  const [showPdfDeleteConfirm, setShowPdfDeleteConfirm] = useState(false);
  const [formErrors, setFormErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [originalEffectiveDate, setOriginalEffectiveDate] = useState(null);

  // Debounce search term
  const debouncedSearchTerm = useDebounce(searchTerm, 300);

  // Export State
  const [selectedRows, setSelectedRows] = useState(new Set());
  const [showExportModal, setShowExportModal] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);

  // Bulk Delete State
  const [showBulkDeleteConfirm, setShowBulkDeleteConfirm] = useState(false);
  const [bulkDeleteLoading, setBulkDeleteLoading] = useState(false);
  const [showBulkUpload, setShowBulkUpload] = useState(false);

  const handleBlur = (field) => {
    setTouched((prev) => ({ ...prev, [field]: true }));
  };

  // Helper function to normalize tags
  const normalizeTags = (tags) => {
    if (Array.isArray(tags)) return tags.filter(Boolean);
    if (typeof tags === "string") {
      return tags
        .split(",")
        .map((t) => t.trim())
        .filter((t) => t.length > 0);
    }
    return [];
  };

  // React Query: Load policy categories
  const { data: categoriesData } = useQuery({
    queryKey: ['policyCategoriesDropdown'],
    queryFn: async () => {
      try {
        const res = await policyCategoryService.getDropdown();
        return res.data || [];
      } catch (error) {
        return [];
      }
    },
    staleTime: 0,
  });

  const categories = categoriesData?.map(c => {
    // Capitalize each word in the category name
    return c.name
      .toLowerCase()
      .split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  }) || [
      "HR Policies",
      "IT & Data Security Policies",
      "Pharmaceutical Compliance",
      "Clinical Safety & Quality",
      "Workplace Safety",
      "Code of Conduct",
      "Finance & Expense Policies",
      "Operations & Production",
      "Research & Development",
      "Environmental & Sustainability",
      "Training & Development",
      "Confidentiality & Data Protection",
      "Other"
    ];


  const applicableRoles = [
    "All Employees",
    "Management",
    "HR Team",
    "IT Team",
    "Finance Team",
    "Sales Team",
    "Operations Team",
  ];


  // React Query: Load departments
  const { data: departments = [] } = useQuery({
    queryKey: ['policyDepartments'],
    queryFn: async () => {
      try {
        // Prefer exact department values from Employee records
        const resEmp = await hrService.getEmployeeDepartmentsDistinct();
        if (resEmp && resEmp.success && Array.isArray(resEmp.data) && resEmp.data.length) {
          return resEmp.data;
        }
        // Fallback to Department master
        const res = await hrService.getDepartments();
        if (res && res.success && res.data) {
          const deptList = Array.isArray(res.data)
            ? res.data
            : Array.isArray(res.data.departments)
              ? res.data.departments
              : [];
          return deptList
            .map((d) => d?.name || d?.departmentName || d?.label || d?.value)
            .filter(Boolean);
        }
        return [];
      } catch (e) {
        return [];
      }
    },
  });

  // React Query: Load acknowledgment stats
  const { data: acknowledgmentStats } = useQuery({
    queryKey: ['acknowledgmentStats'],
    queryFn: async () => {
      try {
        const statsResp = await acknowledgmentService.getAcknowledgmentStats();
        return statsResp?.data || statsResp || {};
      } catch (e) {
        return {};
      }
    },
  });

  // React Query: Load policies
  const {
    data: policiesResult,
    isLoading: loading,
    isError: isErrorLoading,
    error: loadingError,
  } = useQuery({
    queryKey: ['policies', debouncedSearchTerm, filterCategory, filterStatus, startDate, endDate, page, limit],
    queryFn: async () => {
      const response = await policyService.getPolicies({
        category: filterCategory !== "all" ? filterCategory : undefined,
        status: filterStatus !== "all" ? filterStatus : undefined,
        search: debouncedSearchTerm || undefined,
        startDate: startDate || undefined,
        endDate: endDate || undefined,
        page,
        limit,
      });

      // Handle different response structures
      let policiesData = [];
      let pagination = {
        page: page,
        limit: limit,
        total: 0,
        totalPages: 0
      };

      if (response && response.data && Array.isArray(response.data)) {
        policiesData = response.data;
        if (response.pagination) {
          pagination = response.pagination;
        }
      } else if (Array.isArray(response)) {
        policiesData = response;
        pagination.total = response.length;
        pagination.totalPages = Math.ceil(response.length / limit);
      } else if (response && Array.isArray(response.policies)) {
        policiesData = response.policies;
        if (response.pagination) {
          pagination = response.pagination;
        }
      }

      // Fetch acknowledgment stats separately to enrich policies
      let statsData = {};
      try {
        const statsResp = await acknowledgmentService.getAcknowledgmentStats();
        statsData = statsResp?.data || statsResp || {};
      } catch (e) {
      }

      const byPolicy = statsData?.byPolicy || {};

      const merged = (policiesData || []).map((p) => {
        const pid = p.id || p._id || p.policyId;
        const s = pid ? byPolicy[pid] : null;
        const totalFromStats = s?.total ?? undefined;
        const acknowledgedFromStats = s?.acknowledged ?? undefined;
        const totalEmployees = Number(p.totalEmployees ?? totalFromStats ?? 0);
        const acknowledgmentCount = Number(p.acknowledgmentCount ?? acknowledgedFromStats ?? p.acknowledgedEmployees ?? 0);
        const acknowledgmentRate = totalEmployees > 0 ? Math.round((acknowledgmentCount / totalEmployees) * 100) : 0;

        return {
          ...p,
          totalEmployees,
          acknowledgmentCount,
          acknowledgedEmployees: acknowledgmentCount,
          acknowledgmentRate,
          tags: normalizeTags(p.tags),
        };
      });

      return {
        policies: merged,
        pagination: pagination
      };
    },
    enabled: true,
  });

  const policies = policiesResult?.policies || [];
  const pagination = policiesResult?.pagination;

  const clampText = (text = "") => (text || "").trim();
  const isValidStatus = (s) => ["draft", "active", "archived", "under_review", "inactive"].includes(s);
  const isPastDate = (d) => {
    if (!d) return false;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const dd = new Date(d);
    dd.setHours(0, 0, 0, 0);
    return dd < today;
  };
  const isAfter = (a, b) => {
    if (!a || !b) return false;
    return new Date(a).getTime() > new Date(b).getTime();
  };

  // Helper function for text input key press
  const handleTextKeyPress = (e) => {
    // Only allow alphanumeric, spaces, and commas
    if (!/^[a-zA-Z0-9\s,]$/.test(e.key) && !['Backspace', 'Delete', 'Tab', 'Enter', 'ArrowLeft', 'ArrowRight'].includes(e.key)) {
      e.preventDefault();
    }
  };

  const validateForm = (data) => {
    const errors = {};
    // Use originalEffectiveDate from state when validating
    const title = clampText(data.title);
    if (!title) errors.title = "Title is required";
    else if (!/^[a-zA-Z0-9\s,]+$/.test(title)) errors.title = "Title can only contain alphanumeric characters, spaces, and commas";
    else if (title.length < 3) errors.title = "Title must be at least 3 characters";
    else if (title.length > 100) errors.title = "Title must be at most 100 characters";

    if (!data.category) errors.category = "Category is required";

    const description = clampText(data.description);
    if (!description) errors.description = "Description is required";
    else if (!/^[a-zA-Z0-9\s,]+$/.test(description)) errors.description = "Description can only contain alphanumeric characters, spaces, and commas";
    else if (description.length < 20) errors.description = "Minimum 20 characters required";
    else if (description.length > 1000) errors.description = "Maximum 1000 characters allowed";

    // PDF file validation (required for new policies, optional for editing if existing PDF exists)
    if (!data.pdfFile && !data.existingPdfUrl) {
      errors.pdfFile = "PDF file is required";
    } else if (data.pdfFile) {
      if (data.pdfFile.type !== "application/pdf") {
        errors.pdfFile = "Only PDF files are allowed";
      } else if (data.pdfFile.size > 50 * 1024 * 1024) { // 50MB limit
        errors.pdfFile = "PDF file size must be less than 50MB";
      }
    }

    if (!data.effectiveDate) errors.effectiveDate = "Effective date is required";
    else if (originalEffectiveDate) {
      // When editing, ensure the new date is not before the original effective date
      const selectedDate = new Date(data.effectiveDate);
      const originalDate = new Date(originalEffectiveDate);
      selectedDate.setHours(0, 0, 0, 0);
      originalDate.setHours(0, 0, 0, 0);
      if (selectedDate < originalDate) {
        errors.effectiveDate = "Effective date cannot be earlier than the original effective date";
      }
    } else {
      // When creating, ensure the date is not in the past
      if (isPastDate(data.effectiveDate)) {
        errors.effectiveDate = "Effective date cannot be in the past";
      }
    }

    if (!isValidStatus(data.status)) errors.status = "Invalid status";

    return errors;
  };

  useEffect(() => {
    if (initialMode === "create") {
      handleCreatePolicy();
    } else if (id && (initialMode === "edit" || initialMode === "view")) {
      const policy = policies.find(p => (p.id || p._id || p.policyId) === id);
      if (policy) {
        if (initialMode === "edit") handleEditPolicy(policy);
        else handleViewDetails(policy);
      }
    }
  }, [initialMode, id, policies.length]);

  useEffect(() => {
    // Live validation when form is open
    if (showPolicyForm) {
      setFormErrors(validateForm(formData));
    }
  }, [formData, showPolicyForm, originalEffectiveDate]);

  useEffect(() => {
    if (!isListView) return;
    if (isInitialFilterMount.current) {
      isInitialFilterMount.current = false;
      return;
    }
    setPage(1);
  }, [debouncedSearchTerm, filterCategory, filterStatus, startDate, endDate, limit, isListView]);

  // Sync filter/pagination state to URL search params (persists on refresh)
  useEffect(() => {
    if (!isListView) return;

    setSearchParams((prev) => {
      const params = new URLSearchParams(prev);

      const managedKeys = [
        'search',
        'category',
        'status',
        'startDate',
        'endDate',
        'page',
        'limit',
      ];
      managedKeys.forEach((key) => params.delete(key));

      if (debouncedSearchTerm) params.set('search', debouncedSearchTerm);
      if (filterCategory && filterCategory !== 'all') params.set('category', filterCategory);
      if (filterStatus && filterStatus !== 'all') params.set('status', filterStatus);
      if (startDate) params.set('startDate', startDate);
      if (endDate) params.set('endDate', endDate);
      if (page && page !== 1) params.set('page', String(page));
      if (limit && limit !== 10) params.set('limit', String(limit));

      return params;
    }, { replace: true });
  }, [
    debouncedSearchTerm,
    filterCategory,
    filterStatus,
    startDate,
    endDate,
    page,
    limit,
    isListView,
    setSearchParams,
  ]);

  // Filter policies based on search term (client-side filtering for additional search)
  useEffect(() => {
    let filtered = policies || [];

    if (searchTerm) {
      filtered = filtered.filter(
        (policy) =>
          policy.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
          policy.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
          (policy.tags || []).some((tag) =>
            tag.toLowerCase().includes(searchTerm.toLowerCase())
          )
      );
    }

    if (filterCategory !== "all") {
      filtered = filtered.filter(
        (policy) => policy.category === filterCategory
      );
    }

    if (filterStatus !== "all") {
      filtered = filtered.filter((policy) => policy.status === filterStatus);
    }

    setFilteredPolicies(filtered);
  }, [policies, searchTerm, filterCategory, filterStatus]);

  const handleCreatePolicy = () => {
    setEditingPolicy(null);
    setOriginalEffectiveDate(null); // Clear original date for new policy
    setFormData({
      title: "",
      category: "",
      description: "",
      pdfFile: null,
      effectiveDate: "",
      status: "draft",
      existingPdfUrl: null,
      pdfPublicId: null,
    });
    setShowPolicyForm(true);
  };

  // Helper function to format date for date input (YYYY-MM-DD)
  const formatDateForInput = (dateString) => {
    if (!dateString) return "";
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return "";
      // Get local date in YYYY-MM-DD format
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    } catch (error) {
      return "";
    }
  };

  const getPolicyDownloadFilename = (policy, legacyData = null) => {
    const pdfData = legacyData || policy?.pdfFilePath || policy?.pdfUrl;
    if (!pdfData) return "policy.pdf";

    // If pdfData is the metadata object
    if (typeof pdfData === 'object' && pdfData !== null) {
      const fallbackName = pdfData.originalName || pdfData.name || policy?.title || "policy";
      if (/\.[a-zA-Z0-9]+$/.test(fallbackName)) return fallbackName;

      const mimeType = (pdfData.mimetype || pdfData.mimeType || "").toLowerCase();
      if (mimeType === "application/pdf") return `${fallbackName}.pdf`;
      return `${fallbackName}.pdf`;
    }

    // Fallback for string URLs
    const pdfUrl = String(pdfData || '');
    const parts = pdfUrl.split('/');
    const filenameFromUrl = parts[parts.length - 1] || "policy.pdf";
    const baseName = policy?.title || filenameFromUrl;

    if (/\.[a-zA-Z0-9]+$/.test(baseName)) return baseName;
    return `${baseName.toLowerCase().endsWith('.pdf') ? baseName : baseName + '.pdf'}`;
  };

  const handlePolicyDownload = async (url, legacyData = null) => {
    if (!url) return;

    try {
      toast.info('Preparing download...', 2000);

      const fileName = getPolicyDownloadFilename(null, legacyData || url);
      const responseData = await policyService.downloadUrl(url, fileName);

      // Response is a Blob
      const downloadUrl = window.URL.createObjectURL(responseData);

      const link = document.createElement('a');
      link.href = downloadUrl;
      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(downloadUrl);

      toast.success('Download started', 3000);
    } catch (error) {
      // Fallback: Just open in new tab if blob download fails
      window.open(url, '_blank', 'noopener,noreferrer');
    }
  };

  const handleDownloadPolicy = async (policy) => {
    if (!policy) return;

    const policyId = policy.id || policy._id;
    if (policyId) {
      try {
        toast.info('Preparing download...', 2000);

        // Use the backend proxy for robust downloads
        const responseData = await policyService.downloadPolicy(policyId);

        // Response is a Blob (via apiClient interceptor)
        let fileName = getPolicyDownloadFilename(policy);

        const url = window.URL.createObjectURL(responseData);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', fileName);
        document.body.appendChild(link);
        link.click();

        // Clean up
        link.remove();
        window.URL.revokeObjectURL(url);
        toast.success('Download started', 3000);
        return;
      } catch (error) {
      }
    }

    // Fallback for cases where we don't have an ID (e.g. unsaved changes)
    const pdfData = policy.pdfFilePath || policy.pdfUrl;
    const url = typeof pdfData === 'object' ? pdfData.url : pdfData;
    if (url) {
      handlePolicyDownload(url, pdfData);
    } else {
      toast.error('No document available for download');
    }
  };

  const handleEditPolicy = (policy) => {
    setEditingPolicy(policy);
    const originalDate = formatDateForInput(policy.effectiveDate);
    setOriginalEffectiveDate(originalDate); // Store original effective date
    setFormData({
      title: policy.title || "",
      category: policy.category || "",
      description: policy.description || "",
      pdfFile: null, // For editing, user needs to re-upload if they want to change
      effectiveDate: originalDate,
      status: policy.status || "draft",
      existingPdfUrl: policy.pdfFilePath || policy.pdfUrl || null,
      pdfPublicId: policy.pdfPublicId || null,
    });
    setShowPolicyForm(true);
  };

  // React Query: Create/Update policy mutation
  const savePolicyMutation = useMutation({
    mutationFn: async ({ policyId, policyData, pdfFile }) => {
      // Create FormData for file upload
      const formDataToSend = new FormData();

      // Add all policy fields to FormData
      Object.keys(policyData).forEach(key => {
        if (policyData[key] !== null && policyData[key] !== undefined) {
          if (Array.isArray(policyData[key])) {
            policyData[key].forEach((item, index) => {
              formDataToSend.append(`${key}[${index}]`, item);
            });
          } else {
            formDataToSend.append(key, policyData[key]);
          }
        }
      });

      // Add PDF file if provided
      if (pdfFile) {
        formDataToSend.append('pdfFile', pdfFile);
      }

      if (policyId) {
        return await policyService.updatePolicy(policyId, policyData);
      } else {
        return await policyService.createPolicy(policyData);
      }
    },
    onSuccess: (savedPolicy, variables) => {
      queryClient.invalidateQueries({ queryKey: ['policies'] });
      queryClient.invalidateQueries({ queryKey: ['acknowledgmentStats'] });
      toast.success(variables.policyId ? "Policy updated successfully" : "Policy created successfully");
      setShowPolicyForm(false);
      setEditingPolicy(null);
      setFormData({
        title: "",
        category: "",
        existingPdfUrl: null,
        pdfPublicId: null,
      });
      setFormErrors({});
      setTouched({});
      setOriginalEffectiveDate(null);
    },
    onError: (error) => {
      toast.error("Failed to save policy. Please try again.");
    },
  });

  const handleSavePolicy = () => {
    const errors = validateForm(formData);
    setTouched({
      title: true,
      category: true,
      description: true,
      pdfFile: true,
      effectiveDate: true,
      status: true,
    });
    setFormErrors(errors);
    if (Object.keys(errors).length > 0) {
      toast.error("Please fix the highlighted errors");
      return;
    }

    const policyData = {
      title: formData.title.trim(),
      category: formData.category,
      description: formData.description.trim(),
      effectiveDate: formData.effectiveDate,
      status: formData.status,
      pdfFilePath: formData.existingPdfUrl,
      pdfPublicId: formData.pdfPublicId,
    };

    savePolicyMutation.mutate({
      policyId: editingPolicy?.id,
      policyData,
    });
  };

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0] || e.dataTransfer?.files?.[0];
    if (file) {
      if (file.type !== "application/pdf") {
        toast.error("Only PDF files are allowed");
        return;
      }
      if (file.size > 50 * 1024 * 1024) {
        toast.error("PDF file size must be less than 50MB");
        return;
      }

      setUploadingPdf(true);
      try {
        const result = await policyService.uploadCloudinary(file, 'hrms-policies');

        setFormData({
          ...formData,
          pdfFile: null,
          existingPdfUrl: {
            url: result.secure_url || result.url,
            name: file.name,
            originalName: file.name,
            size: file.size,
            mimetype: file.type,
            public_id: result.public_id
          },
          pdfPublicId: result.public_id
        });
        handleBlur("pdfFile");
        toast.success("File uploaded successfully");
      } catch (error) {
        toast.error("Failed to upload PDF. Please try again.");
      } finally {
        setUploadingPdf(false);
      }
    }
  };

  const handleDeletePdf = async () => {
    if (!formData.pdfPublicId) {
      setFormData({ ...formData, pdfFile: null, existingPdfUrl: null, pdfPublicId: null });
      setShowPdfDeleteConfirm(false);
      return;
    }

    setDeletingPdf(true);
    try {
      await policyService.deleteCloudinary(formData.pdfPublicId, 'raw');
      setFormData({ ...formData, pdfFile: null, existingPdfUrl: null, pdfPublicId: null });
      setShowPdfDeleteConfirm(false);
      toast.success("File deleted successfully");
    } catch (error) {
      toast.error("Failed to delete file from Cloudinary");
    } finally {
      setDeletingPdf(false);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    handleFileChange(e);
  };

  const handleDeletePolicy = (policy) => {
    setPolicyToDelete(policy);
    setShowDeleteModal(true);
  };

  // React Query: Delete policy mutation
  const deletePolicyMutation = useMutation({
    mutationFn: async (policyId) => {
      return await policyService.deletePolicy(policyId);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['policies'] });
      queryClient.invalidateQueries({ queryKey: ['acknowledgmentStats'] });
      toast.success("Policy deleted successfully");
      setShowDeleteModal(false);
      setPolicyToDelete(null);
    },
    onError: (error) => {
      toast.error("Failed to delete policy. Please try again.");
    },
  });

  const confirmDeletePolicy = () => {
    if (!policyToDelete) return;
    deletePolicyMutation.mutate(policyToDelete.id);
  };

  const cancelDeletePolicy = () => {
    setShowDeleteModal(false);
    setPolicyToDelete(null);
  };

  // Bulk delete policies handler
  const handleBulkDelete = async () => {
    if (selectedRows.size === 0) return;
    setBulkDeleteLoading(true);
    const ids = Array.from(selectedRows);
    let successCount = 0;
    let failCount = 0;

    for (const id of ids) {
      try {
        await policyService.deletePolicy(id);
        successCount++;
      } catch (error) {
        failCount++;
      }
    }

    setBulkDeleteLoading(false);
    setShowBulkDeleteConfirm(false);
    setSelectedRows(new Set());

    if (successCount > 0) {
      toast.success(`${successCount} policy(ies) deleted successfully${failCount > 0 ? `, ${failCount} failed` : ''}`);
      queryClient.invalidateQueries({ queryKey: ['policies'] });
      queryClient.invalidateQueries({ queryKey: ['acknowledgmentStats'] });
    } else {
      toast.error('Failed to delete selected policies');
    }
  };

  const handleViewDetails = (policy) => {
    setSelectedPolicy(policy);
    setShowDetails(true);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "active":
        return "text-green-600 bg-green-100";
      case "draft":
        return "text-yellow-600 bg-yellow-100";
      case "archived":
        return "text-gray-600 bg-gray-100";
      default:
        return "text-gray-600 bg-gray-100";
    }
  };

  // formatDate is now imported from stringUtils

  const getAcknowledmentProgress = (policy) => {
    if (!policy?.acknowledgmentRequired) return 100;
    const acknowledged = Number(policy?.acknowledgmentCount || policy?.acknowledgedEmployees || 0);
    const total = Number(policy?.totalEmployees || 0);
    if (!total || total <= 0) return 0;
    return Math.round((acknowledged / total) * 100);
  };

  const mapPolicyForExport = (policy) => ({
    'Title': policy.title || '',
    'Category': policy.category || '',
    'Version': policy.version || '1.0',
    'Status': policy.status || '',
    'Effective Date': formatDate(policy.effectiveDate, { includeTime: false }),
    'Created Date': formatDate(policy.createdAt, { includeTime: false }),
    'Total Staff': policy.totalEmployees || 0,
    'Acknowledged Employees': policy.acknowledgmentCount || policy.acknowledgedEmployees || 0,
    'Acknowledgment Rate (%)': `${getAcknowledmentProgress(policy)}%`,
    'Created By': policy.createdBy || '',
    'Approved By': policy.approvedBy || '',
    'Last Updated': formatDate(policy.lastUpdated || policy.updatedAt, { includeTime: false }),
    'PDF URL': typeof (policy.pdfFilePath || policy.pdfUrl) === 'object' ? (policy.pdfFilePath || policy.pdfUrl)?.url : (policy.pdfFilePath || policy.pdfUrl) || ''
  });

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      const allIds = filteredPolicies.map(p => p.id || p._id);
      setSelectedRows(new Set(allIds));
    } else {
      setSelectedRows(new Set());
    }
  };

  const handleSelectRow = (id) => {
    const newSelected = new Set(selectedRows);
    if (newSelected.has(id)) {
      newSelected.delete(id);
    } else {
      newSelected.add(id);
    }
    setSelectedRows(newSelected);
  };

  const handleExport = () => {
    setShowExportModal(true);
  };

  const handleExportSelected = () => {
    const selectedData = filteredPolicies
      .filter(p => selectedRows.has(p.id || p._id))
      .map(mapPolicyForExport);

    exportToCSV(
      selectedData,
      'policies_selected',
      { title: 'Policy Library Export', type: 'Selected Policies' }
    );
  };

  const requestBackendExport = async ({
    scope,
    format,
    overrideFilters = null,
    overridePage = null,
    overrideLimit = null,
  }) => {
    const response = await authFetch(
      `${process.env.REACT_APP_API_BASE_URL}/exports/generate`, {
        credentials: 'include',
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
                  },
        body: JSON.stringify({
          module: 'policy-library',
          format,
          scope,
          filters: overrideFilters ?? {
            category: filterCategory !== 'all' ? filterCategory : undefined,
            status: filterStatus !== 'all' ? filterStatus : undefined,
            search: debouncedSearchTerm || undefined,
            startDate: startDate || undefined,
            endDate: endDate || undefined,
          },
          page: overridePage ?? page,
          limit: overrideLimit ?? limit,
          sortBy: 'createdAt',
          sortOrder: 'desc',
        }),
      }
    );

    if (!response.ok) {
      const text = await response.text();
      throw new Error(text || 'Export request failed');
    }

    const blob = await response.blob();
    const disposition = response.headers.get('content-disposition') || '';
    const filenameMatch =
      disposition.match(/filename\*=UTF-8''([^;]+)/i) ||
      disposition.match(/filename="?([^"]+)"?/i);
    const fallbackName = `policy-library-export.${format === 'xlsx' ? 'xlsx' : format}`;
    const filename = filenameMatch?.[1]
      ? decodeURIComponent(filenameMatch[1]).replace(/(^"|"$)/g, '')
      : fallbackName;

    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  };

  const handleExportCurrentPage = async (scope, format, meta = {}) => {
    if (!['csv', 'xlsx', 'pdf'].includes(format)) return;
    if (!filteredPolicies.length) {
      toast.error('No policies to export on the current page.');
      return;
    }

    setExportLoading(true);
    try {
      await requestBackendExport({
        scope: 'currentPage',
        format,
      });
      setShowExportModal(false);
      toast.success('Export downloaded.');
    } catch (error) {
      toast.error(error?.message || 'Failed to export current page policies');
    } finally {
      setExportLoading(false);
    }
  };

  const handleExportFiltered = async (scope, format, meta = {}) => {
    if (!['csv', 'xlsx', 'pdf'].includes(format)) return;
    if ((pagination?.total ?? policies.length ?? 0) === 0) {
      toast.error('No policies match the current filters.');
      return;
    }

    setExportLoading(true);
    try {
      await requestBackendExport({
        scope: 'filtered',
        format,
      });
      setShowExportModal(false);
      toast.success('Export downloaded.');
    } catch (error) {
      toast.error(error?.message || 'Failed to export filtered policies');
    } finally {
      setExportLoading(false);
    }
  };

  const handleExportRange = async (scope, format, meta = {}) => {
    if (!['csv', 'xlsx', 'pdf'].includes(format)) return;
    const selectedStartDate = meta?.startDate;
    const selectedEndDate = meta?.endDate;

    if (!selectedStartDate || !selectedEndDate) {
      toast.error('Please select start and end dates.');
      return;
    }

    setExportLoading(true);
    try {
      await requestBackendExport({
        scope: 'dateRange',
        format,
        overrideFilters: {
          category: filterCategory !== 'all' ? filterCategory : undefined,
          status: filterStatus !== 'all' ? filterStatus : undefined,
          search: debouncedSearchTerm || undefined,
          startDate: selectedStartDate,
          endDate: selectedEndDate,
        },
      });
      setShowExportModal(false);
      toast.success('Export downloaded.');
    } catch (error) {
      toast.error(error?.message || 'Failed to export policies by date range');
    } finally {
      setExportLoading(false);
    }
  };

  const handleExportAll = async (scope, format, meta = {}) => {
    if (!['csv', 'xlsx', 'pdf'].includes(format)) return;

    setExportLoading(true);
    try {
      await requestBackendExport({
        scope: 'all',
        format,
        overrideFilters: {},
        overridePage: 1,
        overrideLimit: 1000,
      });
      setShowExportModal(false);
      toast.success('Export downloaded.');
    } catch (error) {
      toast.error(error?.message || 'Failed to export all policies');
    } finally {
      setExportLoading(false);
    }
  };



  if (showDetails && selectedPolicy) {
    return (
      <div className="bg-white rounded-lg shadow-sm">
        <div className="p-6 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4 min-w-0">
              <PermissionGate permission="compliance_policy" action="read">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowDetails(false)}
                  className="flex items-center space-x-2 flex-shrink-0"
                >
                  <ArrowLeft className="h-4 w-4" />
                </Button>
              </PermissionGate>
              <div className="min-w-0 flex-1">
                <h1 className="text-2xl font-bold text-gray-900 break-words capitalize">
                  {selectedPolicy.title}
                </h1>
              </div>
            </div>
            <div className="flex items-center space-x-2">
              <span
                className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(
                  selectedPolicy.status
                )}`}
              >
                {(selectedPolicy.status || "").charAt(0).toUpperCase() +
                  (selectedPolicy.status || "").slice(1)}
              </span>
            </div>
          </div>
        </div>

        <div className="p-6">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Main Content - Left Side */}
            <div className="lg:col-span-2 space-y-6">
              {/* Description */}
              <div className="bg-white border border-gray-200 rounded-lg p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-3">
                  Description
                </h3>
                <p className="text-gray-600 leading-relaxed break-words first-letter:capitalize">
                  {selectedPolicy.description || "No description available"}
                </p>
              </div>

              {/* Policy Content */}
              {selectedPolicy.content && (
                <div className="bg-white border border-gray-200 rounded-lg p-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-3">
                    Policy Content
                  </h3>
                  <div className="bg-gray-50 rounded-lg p-4 max-h-96 overflow-y-auto">
                    <p className="text-gray-700 whitespace-pre-wrap leading-relaxed break-words">
                      {selectedPolicy.content}
                    </p>
                  </div>
                </div>
              )}

              {/* PDF Document */}
              {(selectedPolicy.pdfFilePath || selectedPolicy.pdfUrl) && (
                <div className="bg-white border border-gray-200 rounded-lg p-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-3">
                    Policy Document
                  </h3>
                  <div className="flex items-center space-x-4">
                    <FileText className="h-8 w-8 text-blue-600" />
                    <div className="flex-1">
                      <p className="text-sm font-medium text-gray-900">
                        PDF Document Available
                      </p>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleDownloadPolicy(selectedPolicy)}
                      className="flex items-center space-x-2"
                    >
                      <Download className="h-4 w-4" />
                      <span>Download Policy</span>
                    </Button>
                  </div>
                </div>
              )}

              {/* Tags */}
              {selectedPolicy.tags && normalizeTags(selectedPolicy.tags).length > 0 && (
                <div className="bg-white border border-gray-200 rounded-lg p-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-3">
                    Tags
                  </h3>
                  <div className="flex flex-wrap gap-2">
                    {normalizeTags(selectedPolicy.tags).map((tag, index) => (
                      <span
                        key={index}
                        className="px-3 py-1 bg-blue-100 text-blue-800 rounded-md text-sm font-medium"
                      >
                        #{tag}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Sidebar - Right Side */}
            <div className="space-y-6">
              {/* Basic Information */}
              <div className="bg-gray-50 rounded-lg p-6 border border-gray-200">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">
                  Basic Information
                </h3>
                <div className="space-y-4">
                  <div>
                    <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                      Category
                    </label>
                    <p className="text-sm text-gray-900 mt-1 break-words">
                      {selectedPolicy.category || "N/A"}
                    </p>
                  </div>
                  <div>
                    <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                      Status
                    </label>
                    <p className="text-sm mt-1">
                      <span
                        className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(
                          selectedPolicy.status
                        )}`}
                      >
                        {(selectedPolicy.status || "").charAt(0).toUpperCase() +
                          (selectedPolicy.status || "").slice(1)}
                      </span>
                    </p>
                  </div>
                  <div>
                    <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                      Effective Date
                    </label>
                    <p className="text-sm text-gray-900 mt-1 break-words">
                      {selectedPolicy.effectiveDate
                        ? formatDate(selectedPolicy.effectiveDate, { includeTime: false })
                        : "N/A"}
                    </p>
                  </div>
                  {selectedPolicy.acknowledgmentDeadline && (
                    <div>
                      <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                        Acknowledgment Deadline
                      </label>
                      <p className="text-sm text-gray-900 mt-1 break-words">
                        {formatDate(selectedPolicy.acknowledgmentDeadline, {
                          includeTime: false,
                        })}
                      </p>
                    </div>
                  )}
                </div>
              </div>

              {/* Acknowledgment Status */}
              {selectedPolicy.acknowledgmentRequired && (
                <div className="bg-gray-50 rounded-lg p-6 border border-gray-200">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">
                    Acknowledgment Status
                  </h3>
                  <div className="space-y-4">
                    <div>
                      <div className="flex justify-between text-sm mb-2">
                        <span className="text-gray-600">Progress</span>
                        <span className="font-semibold text-gray-900">
                          {getAcknowledmentProgress(selectedPolicy)}%
                        </span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-3">
                        <div
                          className={`h-3 rounded-full transition-all ${getAcknowledmentProgress(selectedPolicy) >= 95
                            ? "bg-green-500"
                            : getAcknowledmentProgress(selectedPolicy) >= 85
                              ? "bg-yellow-500"
                              : "bg-red-500"
                            }`}
                          style={{
                            width: `${getAcknowledmentProgress(selectedPolicy)}%`,
                          }}
                        ></div>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4 pt-2 border-t border-gray-200">
                      <div>
                        <p className="text-xs text-gray-500">Acknowledged</p>
                        <p className="text-lg font-semibold text-gray-900">
                          {selectedPolicy.acknowledgmentCount || 0}
                        </p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500">Total Staff</p>
                        <p className="text-lg font-semibold text-gray-900">
                          {selectedPolicy.totalEmployees || 0}
                        </p>
                      </div>
                    </div>
                    {selectedPolicy.acknowledgmentRate !== undefined && (
                      <div className="pt-2 border-t border-gray-200">
                        <p className="text-xs text-gray-500">Acknowledgment Rate</p>
                        <p className="text-sm font-semibold text-gray-900">
                          {selectedPolicy.acknowledgmentRate.toFixed(1)}%
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Applicable To */}
              {selectedPolicy.applicableTo &&
                selectedPolicy.applicableTo.length > 0 && (
                  <div className="bg-gray-50 rounded-lg p-6 border border-gray-200">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">
                      Applicable To
                    </h3>
                    <div className="space-y-2">
                      {selectedPolicy.applicableTo.map((role, index) => (
                        <div
                          key={index}
                          className="flex items-center space-x-2 p-2 bg-white rounded-md"
                        >
                          <CheckCircle className="h-4 w-4 text-green-600 flex-shrink-0" />
                          <span className="text-sm text-gray-700 break-words flex-1 min-w-0">{role}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

              {/* Metadata */}
              <div className="bg-gray-50 rounded-lg p-6 border border-gray-200">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">
                  Metadata
                </h3>
                <div className="space-y-4">
                  <div>
                    <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                      Created By
                    </label>
                    <p className="text-sm text-gray-900 mt-1 break-words">
                      {selectedPolicy.createdBy || "N/A"}
                    </p>
                  </div>
                  <div>
                    <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                      Created At
                    </label>
                    <p className="text-sm text-gray-900 mt-1 break-words">
                      {selectedPolicy.createdAt ? formatDate(selectedPolicy.createdAt, { includeTime: false }) : "N/A"}
                    </p>
                  </div>
                  {selectedPolicy.updatedBy && (
                    <div>
                      <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                        Last Updated By
                      </label>
                      <p className="text-sm text-gray-900 mt-1 break-words">
                        {selectedPolicy.updatedBy}
                      </p>
                    </div>
                  )}
                  {selectedPolicy.createdAt && (
                    <div>
                      <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                        Created At
                      </label>
                      <p className="text-sm text-gray-900 mt-1 break-words">
                        {formatDate(selectedPolicy.createdAt, { includeTime: true })}
                      </p>
                    </div>
                  )}
                  {selectedPolicy.updatedAt && (
                    <div>
                      <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                        Last Updated
                      </label>
                      <p className="text-sm text-gray-900 mt-1 break-words">
                        {formatDate(selectedPolicy.updatedAt, { includeTime: true })}
                      </p>
                    </div>
                  )}
                  {selectedPolicy.lastUpdated && (
                    <div>
                      <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                        Last Updated Date
                      </label>
                      <p className="text-sm text-gray-900 mt-1 break-words">
                        {formatDate(selectedPolicy.lastUpdated, { includeTime: true })}
                      </p>
                    </div>
                  )}
                  {selectedPolicy.downloadCount !== undefined && (
                    <div>
                      <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                        Download Count
                      </label>
                      <p className="text-sm text-gray-900 mt-1 break-words">
                        {selectedPolicy.downloadCount || 0}
                      </p>
                    </div>
                  )}
                </div>
              </div>

              {/* Actions */}
              <div className="space-y-2">
                <PermissionGate permission="compliance_policy" action="read">
                  <Button
                    className="w-full flex items-center justify-center space-x-2"
                    onClick={() => handleDownloadPolicy(selectedPolicy)}
                  >
                    <Download className="h-4 w-4" />
                    <span>Download Policy</span>
                  </Button>
                </PermissionGate>
                {(userRole === "super_admin" || userRole === "hr_manager") && (
                  <PermissionGate permission="compliance_policy" action="update">
                    <Button
                      variant="outline"
                      className="w-full flex items-center justify-center space-x-2"
                      onClick={() => handleEditPolicy(selectedPolicy)}
                    >
                      <Edit className="h-4 w-4" />
                      <span>Edit Policy</span>
                    </Button>
                  </PermissionGate>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (showPolicyForm) {
    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        {/* Simple Header */}
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <h1 className="text-xl font-semibold text-gray-900">
              {editingPolicy ? "Edit Policy" : "Create New Policy"}
            </h1>
            <PermissionGate permission="compliance_policy" action="read">
              <Button
                variant="outline"
                onClick={() => setShowPolicyForm(false)}
                className="flex items-center space-x-2"
              >
                <X className="h-4 w-4" />
                <span>Cancel</span>
              </Button>
            </PermissionGate>
          </div>
        </div>

        {/* Form Content */}
        <div className="p-6">
          <div className="space-y-6">
            {/* Basic Information Section */}
            <div className="space-y-5">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Policy Title <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => {
                    const value = e.target.value.replace(/[^a-zA-Z0-9\s,]/g, '').slice(0, 100);
                    setFormData({ ...formData, title: value });
                  }}
                  onKeyPress={handleTextKeyPress}
                  maxLength={100}
                  required
                  onBlur={() => handleBlur("title")}
                  className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${(touched.title || savePolicyMutation.isPending) && formErrors.title
                    ? "border-red-500"
                    : "border-gray-300"
                    }`}
                  placeholder="Enter policy title"
                />
                <div className="flex items-center justify-between mt-1">
                  <div>
                    {(touched.title || savePolicyMutation.isPending) && formErrors.title && (
                      <p className="text-xs text-red-600">{formErrors.title}</p>
                    )}
                  </div>
                  <p className="text-xs text-gray-500">
                    {formData.title.length}/100 characters
                  </p>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Category <span className="text-red-500">*</span>
                </label>
                <select
                  value={formData.category}
                  onChange={(e) =>
                    setFormData({ ...formData, category: e.target.value })
                  }
                  onBlur={() => handleBlur("category")}
                  className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white ${(touched.category || savePolicyMutation.isPending) && formErrors.category
                    ? "border-red-500"
                    : "border-gray-300"
                    }`}
                >
                  <option value="">Select category</option>
                  {categories.map((category) => {
                    // Extract the category name (before the comment)
                    const categoryName = category.split('//')[0].trim();
                    return (
                      <option key={categoryName} value={categoryName}>
                        {categoryName}
                      </option>
                    );
                  })}
                </select>
                {(touched.category || savePolicyMutation.isPending) && formErrors.category && (
                  <p className="mt-1 text-xs text-red-600">{formErrors.category}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Brief Description <span className="text-red-500">*</span>
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => {
                    const value = e.target.value.replace(/[^a-zA-Z0-9\s,]/g, '').slice(0, 1000);
                    setFormData({ ...formData, description: value });
                  }}
                  onBlur={() => handleBlur("description")}
                  onKeyPress={handleTextKeyPress}
                  maxLength={1000}
                  rows={8}
                  required
                  className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none ${(touched.description || savePolicyMutation.isPending) && formErrors.description
                    ? "border-red-500"
                    : "border-gray-300"
                    }`}
                  placeholder="Brief description of the policy"
                />
                <div className="flex items-center justify-between mt-1">
                  <div>
                    {(touched.description || savePolicyMutation.isPending) && formErrors.description && (
                      <p className="text-xs text-red-600">{formErrors.description}</p>
                    )}
                  </div>
                  <p className="text-xs text-gray-500">
                    {formData.description.length}/1000 characters
                  </p>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Effective Date <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="date"
                    value={formData.effectiveDate}
                    min={originalEffectiveDate || new Date().toISOString().split('T')[0]}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        effectiveDate: e.target.value,
                      })
                    }
                    onBlur={() => handleBlur("effectiveDate")}
                    onClick={(e) => e.target.showPicker && e.target.showPicker()}
                    className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${(touched.effectiveDate || savePolicyMutation.isPending) && formErrors.effectiveDate
                      ? "border-red-500"
                      : "border-gray-300"
                      }`}
                  />
                  {(touched.effectiveDate || savePolicyMutation.isPending) && formErrors.effectiveDate && (
                    <p className="mt-1 text-xs text-red-600">{formErrors.effectiveDate}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Status
                  </label>
                  <select
                    value={formData.status}
                    onChange={(e) =>
                      setFormData({ ...formData, status: e.target.value })
                    }
                    onBlur={() => handleBlur("status")}
                    className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white ${(touched.status || savePolicyMutation.isPending) && formErrors.status
                      ? "border-red-500"
                      : "border-gray-300"
                      }`}
                  >
                    <option value="draft">Draft</option>
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                  </select>
                  {(touched.status || savePolicyMutation.isPending) && formErrors.status && (
                    <p className="mt-1 text-xs text-red-600">{formErrors.status}</p>
                  )}
                </div>
              </div>

              {/* Policy Document Section - Moved to Bottom */}
              <div className="pt-6 border-t border-gray-200">
                <label className="block text-sm font-medium text-gray-700 mb-3">
                  Policy Document (PDF) <span className="text-red-500">*</span>
                </label>
                <div
                  className={`border-2 border-dashed rounded-lg p-6 text-center hover:border-blue-400 transition-colors cursor-pointer ${(touched.pdfFile || savePolicyMutation.isPending) && formErrors.pdfFile
                    ? "border-red-500 bg-red-50"
                    : "border-gray-300"
                    }`}
                  onDragOver={handleDragOver}
                  onDrop={handleDrop}
                  onClick={() => document.getElementById('pdf-upload')?.click()}
                >
                  <input
                    type="file"
                    id="pdf-upload"
                    accept=".pdf"
                    onChange={handleFileChange}
                    className="hidden"
                  />
                  <div className="flex flex-col items-center">
                    {uploadingPdf ? (
                      <>
                        <Loader2 className="h-10 w-10 text-blue-500 animate-spin mb-3" />
                        <p className="text-sm font-medium text-gray-900 mb-1">
                          Uploading...
                        </p>
                        <p className="text-xs text-gray-500">
                          Please wait while we process your file
                        </p>
                      </>
                    ) : formData.existingPdfUrl ? (
                      <>
                        <FileText className="h-10 w-10 text-green-500 mb-3" />
                        <p className="text-sm font-medium text-gray-900 mb-1">
                          PDF Document Uploaded
                        </p>
                        <p className="text-xs text-gray-500 mb-3">
                          {typeof formData.existingPdfUrl === 'object' && formData.existingPdfUrl !== null
                            ? formData.existingPdfUrl.name || 'document.pdf'
                            : 'File is securely stored on Cloudinary'}
                        </p>
                        <div className="flex gap-4">
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              const currentPdfUrl = typeof formData.existingPdfUrl === 'object' && formData.existingPdfUrl !== null ? formData.existingPdfUrl.url : formData.existingPdfUrl;
                              handlePolicyDownload(currentPdfUrl, formData.existingPdfUrl);
                            }}
                            className="flex items-center text-sm text-blue-600 hover:text-blue-700 font-medium"
                          >
                            <Download className="h-4 w-4 mr-1" />
                            Download PDF
                          </button>
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              setShowPdfDeleteConfirm(true);
                            }}
                            className="flex items-center text-sm text-red-600 hover:text-red-700 font-medium"
                          >
                            <Trash2 className="h-4 w-4 mr-1" />
                            Delete
                          </button>
                        </div>
                      </>
                    ) : (
                      <>
                        <Upload className="h-10 w-10 text-gray-400 mb-3" />
                        <p className="text-sm text-gray-600 mb-2">
                          Click to upload or drag and drop
                        </p>
                        <p className="text-xs text-gray-500 mb-3">
                          PDF files only (Max 50MB)
                        </p>
                        <span className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors pointer-events-none">
                          <Upload className="h-4 w-4 mr-2" />
                          Upload PDF
                        </span>
                      </>
                    )}
                  </div>
                </div>
                {(touched.pdfFile || savePolicyMutation.isPending) && formErrors.pdfFile && (
                  <p className="mt-2 text-xs text-red-600">{formErrors.pdfFile}</p>
                )}

                {/* PDF Delete Confirmation Modal */}
                {showPdfDeleteConfirm && (
                  <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[70]">
                    <div className="bg-white rounded-lg shadow-xl max-w-sm w-full mx-4 p-6">
                      <div className="flex items-center space-x-3 mb-4">
                        <div className="bg-red-100 p-2 rounded-full">
                          <AlertCircle className="h-6 w-6 text-red-600" />
                        </div>
                        <h3 className="text-lg font-semibold text-gray-900">Delete PDF File?</h3>
                      </div>
                      <p className="text-sm text-gray-500 mb-6">
                        This will permanently remove the document from Cloudinary. This action cannot be undone.
                      </p>
                      <div className="flex justify-end space-x-3">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setShowPdfDeleteConfirm(false)}
                          disabled={deletingPdf}
                        >
                          Cancel
                        </Button>
                        <Button
                          variant="danger"
                          size="sm"
                          onClick={handleDeletePdf}
                          className="bg-red-600 hover:bg-red-700 text-white min-w-[100px]"
                          disabled={deletingPdf}
                        >
                          {deletingPdf ? (
                            <div className="flex items-center">
                              <Loader2 className="h-4 w-4 animate-spin mr-2" />
                              Deleting...
                            </div>
                          ) : (
                            "Confirm Delete"
                          )}
                        </Button>
                      </div>
                    </div>
                  </div>
                )}
              </div>

            </div>
          </div>

          {/* Action Buttons */}
          <div className="mt-6 pt-6 border-t border-gray-200 flex justify-end space-x-3">
            <PermissionGate permission="compliance_policy" action="read">
              <Button
                variant="outline"
                onClick={() => setShowPolicyForm(false)}
              >
                Cancel
              </Button>
            </PermissionGate>
            <PermissionGate permission="compliance_policy" action={editingPolicy ? "update" : "create"}>
              <Button
                onClick={handleSavePolicy}
                disabled={savePolicyMutation.isPending}
                className="flex items-center space-x-2"
              >
                {savePolicyMutation.isPending ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    <span>Saving...</span>
                  </>
                ) : (
                  <>
                    <Save className="h-4 w-4" />
                    <span>
                      {editingPolicy ? "Update Policy" : "Create Policy"}
                    </span>
                  </>
                )}
              </Button>
            </PermissionGate>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-sm">
      <div className="p-6 border-b border-gray-200">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Policy Library</h1>
              <p className="text-gray-600">Manage company policies and documentation</p>
            </div>
          </div>
          <div className="flex items-center space-x-4">
            <PermissionGate permission="compliance_policy" action="read">
              <Button
                variant="outline"
                className="flex items-center space-x-2"
                onClick={selectedRows.size > 0 ? handleExportSelected : handleExport}
              >
                <Download className="h-4 w-4" />
                <span>{selectedRows.size > 0 ? `Export Selected (${selectedRows.size})` : 'Export'}</span>
              </Button>
            </PermissionGate>
            {selectedRows.size > 0 && (
              <PermissionGate permission="compliance_policy" action="delete">
                <Button
                  variant="danger"
                  className="flex items-center space-x-2 bg-red-600 hover:bg-red-700 text-white rounded-md min-w-[140px]"
                  onClick={() => setShowBulkDeleteConfirm(true)}
                  loading={bulkDeleteLoading}
                >
                  <Trash2 className="h-4 w-4" />
                  <span>Delete Selected ({selectedRows.size})</span>
                </Button>
              </PermissionGate>
            )}
            <PermissionGate permission="compliance_policy" action="create">
              <Button
                variant="outline"
                onClick={() => setShowBulkUpload(true)}
                className="flex items-center space-x-2 border-green-500 text-green-600 hover:bg-green-50 px-4 py-2 rounded-md font-medium"
              >
                <Upload className="h-4 w-4" />
                <span>Bulk Upload</span>
              </Button>
            </PermissionGate>
            <PermissionGate permission="compliance_policy" action="create">
              <Button
                onClick={handleCreatePolicy}
                className="flex items-center space-x-2 bg-agrosaf-green hover:bg-agrosaf-darkGreen text-white px-4 py-2 rounded-md font-medium focus:ring-agrosaf-green"
              >
                <Plus className="h-4 w-4" />
                <span>Create Policy</span>
              </Button>
            </PermissionGate>
          </div>
        </div>
      </div>

      <div className="p-6">
        {isErrorLoading && (
          <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-md">
            <div className="flex items-center">
              <AlertCircle className="h-5 w-5 text-red-400 mr-2" />
              <p className="text-red-700">
                {loadingError?.message || "Failed to load policies. Please try again."}
              </p>
            </div>
          </div>
        )}

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-6">
          <div className="flex flex-wrap items-end gap-4 w-full">
            {/* Search Bar */}
            <div className="relative flex-[2] min-w-[200px]">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
              <input
                type="text"
                placeholder="Search Policy Name..."
                value={searchTerm}
                onChange={(e) => {
                  const value = e.target.value.replace(/[^a-zA-Z0-9\s@._+-]/g, '').slice(0, 100);
                  setSearchTerm(value);
                }}
                maxLength={100}
                className="w-full pl-10 pr-10 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-agrosaf-green focus:border-agrosaf-green"
              />
              {searchTerm && (
                <button
                  onClick={() => setSearchTerm('')}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <X className="h-4 w-4" />
                </button>
              )}
            </div>

            {/* Category Filter */}
            <div className="flex-shrink-0">
              <select
                value={filterCategory}
                onChange={(e) => setFilterCategory(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-agrosaf-green focus:border-agrosaf-green"
              >
                <option value="all">All Categories</option>
                {categories.map((category) => (
                  <option key={category} value={category}>
                    {category}
                  </option>
                ))}
              </select>
            </div>

            {/* Status Filter */}
            <div className="flex-shrink-0">
              <select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-agrosaf-green focus:border-agrosaf-green"
              >
                <option value="all">All Status</option>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
                <option value="draft">Draft</option>
              </select>
            </div>

            {/* Created Date Filter */}
            <div className="flex-shrink-0">
              <DateRangeFilter
                startDate={startDate}
                endDate={endDate}
                onChange={({ startDate, endDate }) => {
                  setStartDate(startDate);
                  setEndDate(endDate);
                }}
                containerClassName="flex items-center space-x-2"
              />
            </div>

            {/* Select All */}
            <div className="flex items-center space-x-2 bg-gray-50 px-3 py-2 rounded-lg border border-gray-200">
              <input
                type="checkbox"
                id="select-all"
                checked={filteredPolicies.length > 0 && selectedRows.size === filteredPolicies.length}
                onChange={handleSelectAll}
                className="rounded border-gray-300 text-agrosaf-green focus:ring-agrosaf-green h-4 w-4"
              />
              <label htmlFor="select-all" className="text-sm font-medium text-gray-700 cursor-pointer">
                Select All
              </label>
            </div>

            {/* Clear Filters Button */}
            {(searchTerm || filterCategory !== 'all' || filterStatus !== 'all' || startDate || endDate || selectedRows.size > 0) && (
              <div className="relative flex-shrink-0">
                <Button
                  variant="outline"
                  onClick={() => {
                    setSearchTerm('');
                    setFilterCategory('all');
                    setFilterStatus('all');
                    setStartDate('');
                    setEndDate('');
                    setSelectedRows(new Set());
                  }}
                  className="flex items-center space-x-2"
                >
                  <X className="h-4 w-4" />
                  <span>Clear</span>
                </Button>
              </div>
            )}
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="flex items-center space-x-2">
              <Loader2 className="h-6 w-6 animate-spin text-agrosaf-green" />
              <span className="text-gray-600 font-medium">Loading policies...</span>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {(filteredPolicies || []).map((policy) => (
              <div
                key={policy.id || policy._id}
                className="border border-gray-200 rounded-lg p-6 hover:shadow-lg transition-all duration-200 bg-white hover:border-agrosaf-green/30"
              >
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      checked={selectedRows.has(policy.id || policy._id)}
                      onChange={() => handleSelectRow(policy.id || policy._id)}
                      className="rounded border-gray-300 text-agrosaf-green focus:ring-agrosaf-green h-4 w-4 mr-2"
                    />
                    <FileText className="h-5 w-5 text-blue-600" />
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(policy.status)}`}>
                      {(policy.status || "").charAt(0).toUpperCase() + (policy.status || "").slice(1)}
                    </span>
                  </div>
                  <div className="flex items-center space-x-1">
                    <PermissionGate permission="compliance_policy" action="read">
                      <Button variant="ghost" size="sm" onClick={() => handleViewDetails(policy)} className="p-2 hover:bg-blue-50 text-blue-600 rounded-md" title="View Details">
                        <Eye className="h-4 w-4" />
                      </Button>
                    </PermissionGate>
                    <PermissionGate permission="compliance_policy" action="update">
                      <Button variant="ghost" size="sm" onClick={() => handleEditPolicy(policy)} className="p-2 hover:bg-green-50 text-agrosaf-green rounded-md" title="Edit Policy">
                        <Edit className="h-4 w-4" />
                      </Button>
                    </PermissionGate>
                    <PermissionGate permission="compliance_policy" action="delete">
                      <Button variant="ghost" size="sm" onClick={() => handleDeletePolicy(policy)} className="p-2 hover:bg-red-50 text-red-600 rounded-md" title="Delete Policy">
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </PermissionGate>
                  </div>
                </div>

                <h3 className="font-semibold text-gray-900 mb-2 truncate capitalize" title={policy.title}>{policy.title}</h3>
                <p className="text-sm text-gray-600 mb-3 line-clamp-2 first-letter:capitalize">{policy.description}</p>

                <div className="space-y-2 text-xs text-gray-500">
                  <div className="flex items-center justify-between">
                    <span>Category:</span>
                    <span className="font-medium text-gray-700 capitalize">{policy.category}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span>Effective:</span>
                    <span className="font-medium text-gray-700">{formatDate(policy.effectiveDate, { includeTime: false })}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span>Created Date:</span>
                    <span className="font-medium text-gray-700">{formatDate(policy.createdAt, { includeTime: true })}</span>
                  </div>
                  {policy.acknowledgmentRequired && (
                    <div className="mt-3 p-2 bg-gray-50 rounded-md">
                      <div className="flex justify-between text-xs mb-2">
                        <span className="font-medium text-gray-700">Acknowledgments</span>
                        <span className="font-semibold text-agrosaf-green">{getAcknowledmentProgress(policy)}%</span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-2">
                        <div
                          className="bg-gradient-to-r from-agrosaf-green to-agrosaf-darkGreen h-2 rounded-full transition-all duration-300"
                          style={{ width: `${getAcknowledmentProgress(policy)}%` }}
                        ></div>
                      </div>
                      <div className="text-xs text-gray-500 mt-1">
                        {policy.acknowledgmentCount} of {policy.totalEmployees} employees
                      </div>
                    </div>
                  )}
                </div>

                <div className="mt-4 flex flex-wrap gap-2">
                  {(policy.tags || []).slice(0, 3).map((tag, index) => (
                    <span key={index} className="px-2 py-1 bg-blue-50 text-blue-600 rounded-full text-xs font-medium border border-blue-100">#{tag}</span>
                  ))}
                  {(policy.tags || []).length > 3 && (
                    <span className="px-2 py-1 bg-gray-50 text-gray-500 rounded-full text-xs font-medium border border-gray-100">+{(policy.tags || []).length - 3}</span>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        {!loading && (filteredPolicies || []).length > 0 && pagination && (
          <div className="mt-6">
            <PaginationControls
              currentPage={page}
              totalPages={pagination.totalPages || 0}
              totalItems={pagination.total || 0}
              limit={limit}
              onPageChange={(newPage) => setPage(newPage)}
              onLimitChange={(newLimit) => {
                setLimit(newLimit);
                setPage(1);
              }}
              hasNextPage={page < (pagination.totalPages || 0)}
              hasPrevPage={page > 1}
            />
          </div>
        )}

        {!loading && (filteredPolicies || []).length === 0 && (
          <div className="text-center py-12">
            <FileText className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">No policies found</h3>
            <p className="mt-1 text-sm text-gray-500">
              {searchTerm || filterCategory !== "all" || filterStatus !== "all" || startDate || endDate
                ? "Try adjusting your search or filter criteria."
                : "Get started by creating your first policy."}
            </p>
          </div>
        )}
      </div>

      {
        showDeleteModal && (
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl shadow-2xl max-w-md w-full overflow-hidden">
              <div className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center space-x-3">
                    <div className="p-2 bg-yellow-50 rounded-lg">
                      <AlertCircle className="h-6 w-6 text-yellow-600" />
                    </div>
                    <h3 className="text-lg font-bold text-gray-900">Confirm Deletion</h3>
                  </div>
                  <button onClick={cancelDeletePolicy} className="text-gray-400 hover:text-gray-600 transition-colors">
                    <X className="h-5 w-5" />
                  </button>
                </div>

                <div className="mb-6">
                  <p className="text-gray-600">Are you sure you want to delete this policy? This action cannot be undone.</p>
                  {policyToDelete && (
                    <div className="mt-4 p-4 bg-gray-50 rounded-lg border border-gray-100">
                      <p className="text-sm font-semibold text-gray-900">{policyToDelete.title}</p>
                      <p className="text-xs text-gray-500 mt-1">{policyToDelete.category}</p>
                    </div>
                  )}
                </div>

                <div className="flex justify-end gap-3 mt-8">
                  <Button variant="outline" onClick={cancelDeletePolicy} disabled={deletePolicyMutation.isPending}>
                    Cancel
                  </Button>
                  <Button variant="danger" onClick={confirmDeletePolicy} disabled={deletePolicyMutation.isPending} className="bg-red-600 hover:bg-red-700 text-white min-w-[100px]">
                    {deletePolicyMutation.isPending ? (
                      <div className="flex items-center justify-center">
                        <Loader2 className="h-4 w-4 animate-spin mr-2" />
                        <span>Deleting...</span>
                      </div>
                    ) : (
                      "Delete Policy"
                    )}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )
      }

      <ExportModal
        isOpen={showExportModal}
        onClose={() => setShowExportModal(false)}
        onExportCurrentPage={handleExportCurrentPage}
        onExportFiltered={handleExportFiltered}
        onExportRange={handleExportRange}
        onExportAll={handleExportAll}
        title="Export Policy Library"
        loading={exportLoading}
        scopedFormatExport={{
          onExport: async (scope, format, meta) => {
            if (scope === 'currentPage') {
              return handleExportCurrentPage(scope, format, meta);
            }
            if (scope === 'filtered') {
              return handleExportFiltered(scope, format, meta);
            }
            if (scope === 'dateRange') {
              return handleExportRange(scope, format, meta);
            }
            if (scope === 'all') {
              return handleExportAll(scope, format, meta);
            }
          },
          formats: ['csv', 'xlsx', 'pdf'],
          showCurrentPage: true,
          showFiltered: true,
          showDateRange: true,
          showAll: true,
        }}
      />

      {/* Bulk Delete Confirmation Modal */}
      {
        showBulkDeleteConfirm && (
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-[60] p-4">
            <div className="bg-white rounded-xl shadow-2xl max-w-md w-full overflow-hidden">
              <div className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center space-x-3">
                    <div className="p-2 bg-red-50 rounded-lg">
                      <Trash2 className="h-6 w-6 text-red-600" />
                    </div>
                    <h3 className="text-lg font-bold text-gray-900">Confirm Bulk Deletion</h3>
                  </div>
                  <button
                    onClick={() => setShowBulkDeleteConfirm(false)}
                    className="text-gray-400 hover:text-gray-600 transition-colors"
                  >
                    <X className="h-5 w-5" />
                  </button>
                </div>

                <div className="mb-6">
                  <p className="text-gray-600">
                    Are you sure you want to delete <span className="font-bold text-red-600">{selectedRows.size}</span> selected policies?
                  </p>
                  <p className="text-sm text-gray-500 mt-2">
                    This action cannot be undone and will permanently remove all selected policy data.
                  </p>
                </div>

                <div className="flex justify-end gap-3 mt-8">
                  <Button
                    variant="outline"
                    onClick={() => setShowBulkDeleteConfirm(false)}
                    disabled={bulkDeleteLoading}
                  >
                    Cancel
                  </Button>
                  <PermissionGate permission="compliance_policy" action="delete">
                    <Button
                      variant="danger"
                      onClick={handleBulkDelete}
                      disabled={bulkDeleteLoading}
                      className="bg-red-600 hover:bg-red-700 text-white min-w-[120px]"
                    >
                      {bulkDeleteLoading ? (
                        <div className="flex items-center justify-center">
                          <Loader2 className="h-4 w-4 animate-spin mr-2" />
                          <span>Deleting...</span>
                        </div>
                      ) : (
                        `Delete ${selectedRows.size} Policies`
                      )}
                    </Button>
                  </PermissionGate>
                </div>
              </div>
            </div>
          </div>
        )
      }
      {showBulkUpload && (
        <BulkUploadModal
          isOpen={showBulkUpload}
          onClose={() => setShowBulkUpload(false)}
          onSuccess={() => queryClient.invalidateQueries({ queryKey: ['policies'] })}
          config={{
            title: "Bulk Upload Policies",
            subtitle: "Import multiple company policies using a CSV file",
            templateEndpoint: "/policies/bulk-upload/template",
            validateEndpoint: "/policies/bulk-upload/validate",
            uploadEndpoint: "/policies/bulk-upload",
            templateFilename: "policy_bulk_upload_template.csv",
            dataKey: "data",
            maxRecords: 100,
            instructions: [
              "Title and Category are required.",
              "Description is required (minimum 20 characters).",
              "Effective Date is required (YYYY-MM-DD format).",
              "Status is optional (defaults to 'draft'). Values: draft, active, inactive.",
              "PDF URL is optional.",
              "Acknowledgment Required is optional (TRUE/FALSE, defaults to TRUE)."
            ]
          }}
        />
      )}
    </div >
  );
};

export default PolicyLibrary;
